// TODO: W-13933886 - Localize initial show request strings
// After create session, get localized strings for the following divs: tabBootErrTitle, tabBootErrBody
// What about on error, before the tsConfig is sent?
// it looks like in _some_ contexts we get a 'tableauLang'
// cookie (and a 'tableau_locale' cookie). I can find references to the latter in code,
// but not tableauLang. Is that a legacy cookie that we used to serve for Online?

import * as guid from "guid";
import { TsConfig } from "./API/TsConfig";
import { onDocumentReady } from "./Async";
import { bootstrap } from "./Bootstrap";
import { handleJQueryXhrError, handleXhrError } from "./HandleXhrResponses";
import {
  findFirstSupportedLanguageWithRegionAgnosticFallbackOrDefault,
  getUserPreferredBrowserLanguages,
} from "./Localization";
import { SessionRoute } from "./SessionRoute";
import { startSession } from "./StartSession";
import {
  fireScriptReady,
  fireViewerBootstrapLoaded,
  getServerLanguageAndLocale,
  loadStaticAssets,
  onViewerBootstrapLoaded,
  onVqlScriptReady,
  preloadStaticAssets,
} from "./StaticAssets";
import { StaticConfig, isJsDebug, readStaticConfig } from "./StaticConfig";
import { VizqlStorage } from "./Storage";
import { TableauLocalizedStrings, loadStrings } from "./Strings";
import { VizqlPaths } from "./VizqlPaths";
import {
  IWebClientMetricsLogger,
  getFriendlyEventDescription,
} from "./telemetry/IWebClientMetricsLogger";
import { LocalWebClientMetricsLogger } from "./telemetry/LocalWebClientMetricsLogger";
import {
  LocalMetricsContext,
  MetricsContext,
  createContext,
  getContextIdThenIncrement,
  getMetricsSuitesFilter,
  logMetricsEvent,
  setMetricsEventLoggers,
  setMetricsSuitesFilter,
  setSessionType,
} from "./telemetry/MetricsContext";
import {
  MetricsEvent,
  MetricsEventParameters,
  MetricsEventType,
  getMetricsParameterShortName,
} from "./telemetry/MetricsEvent";
import { MetricsSuites } from "./telemetry/MetricsSuites";
import {
  EptBreakdownMetricNames,
  closeEptMetricsContext,
  getDuringEptFlag,
  getEptBreakdownMetricsContexts,
  getIsPageVisibleWhenStartLoading,
  getOffsetFromTimeZero,
  getPageToHiddenCount,
  getPageVisibilityMetricsContexts,
  getUniqueKey,
  newUuid,
  uuidToHex32,
  onVisibilityChange,
  openEptMetricsContext,
  markEptMetricsEvent,
  setDuringEptFlag,
} from "./telemetry/TelemetryBreakdown";
import { updateDocumentTitle } from "./views/Content";
import { ErrorDialog } from "./views/ErrorDialog";
import { getSizeConstraints } from "./views/GetSizeConstraints";
import {
  handleTsConfigForTableauPublic,
  updateInitialPublicPage,
} from "./views/Public";
import { showSpinner } from "./views/Spinner";
import { setupTypeStyle } from "./views/TypeStyleSetup";

function storage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const vizqlStorage = new VizqlStorage(storage());

export {
  EptBreakdownMetricNames,
  IWebClientMetricsLogger,
  LocalMetricsContext,
  LocalWebClientMetricsLogger,
  MetricsContext,
  MetricsEvent,
  MetricsEventParameters,
  MetricsEventType,
  MetricsSuites,
  SessionRoute,
  VizqlPaths,
  closeEptMetricsContext,
  createContext,
  fireScriptReady,
  fireViewerBootstrapLoaded,
  getContextIdThenIncrement,
  getDuringEptFlag,
  getEptBreakdownMetricsContexts,
  getFriendlyEventDescription,
  getIsPageVisibleWhenStartLoading,
  getMetricsParameterShortName,
  getMetricsSuitesFilter,
  getOffsetFromTimeZero,
  getPageToHiddenCount,
  getPageVisibilityMetricsContexts,
  getSizeConstraints,
  getUniqueKey,
  newUuid,
  uuidToHex32,
  handleJQueryXhrError,
  handleXhrError,
  logMetricsEvent,
  markEptMetricsEvent,
  onViewerBootstrapLoaded,
  onVqlScriptReady,
  openEptMetricsContext,
  parseTsConfigFromPage,
  setDuringEptFlag,
  setMetricsEventLoggers,
  setMetricsSuitesFilter,
  vizqlStorage,
};

async function doShow(
  routingKey?: SessionRoute | null,
  authToken?: string | null
): Promise<void> {
  showSpinner(document);
  preloadStaticAssets(document, isJsDebug(document) || false);

  const tsConfig = await startSession(
    routingKey || null,
    authToken || null,
    document,
    document.location
  );

  try {
    handleTsConfig(false, tsConfig);
  } catch (reason) {
    reportError(reason);
  }
}

async function doShowOffline(): Promise<null> {
  return Promise.resolve(parseTsConfigFromPage()).then(
    (tsConfig: TsConfig | null) => {
      updatePageBasedOnTsConfig(false, tsConfig);
      return null;
    }
  );
}

function parseTsConfigFromPage(): TsConfig {
  const container = document.getElementById(
    "tsConfigContainer"
  ) as HTMLTextAreaElement;
  return JSON.parse(container.innerText) as TsConfig;
}

function writeTsConfigIntoPage(tsConfig: TsConfig) {
  (document.getElementById("tsConfigContainer") as HTMLTextAreaElement).value =
    JSON.stringify(tsConfig);

  document.dispatchEvent(new CustomEvent("tsConfigLoaded"));

  return tsConfig;
}

function templateHtmlBasedOnTsConfig(
  isUnitTestRun: boolean,
  tsConfig: TsConfig | null
) {
  if (!tsConfig) {
    // This won't show an error dialog; we have to assume that there is some redirect
    // in process that will prevent the user from sitting with a blank page forever.
    return;
  }
  writeTsConfigIntoPage(tsConfig);
  updatePageBasedOnTsConfig(isUnitTestRun, tsConfig);
}

function updatePageBasedOnTsConfig(
  isUnitTestRun: boolean,
  tsConfig: TsConfig | null
) {
  if (!tsConfig) {
    return;
  }

  if (!isUnitTestRun) {
    loadStaticAssets(document, tsConfig, isJsDebug(document) || false);
  }

  handleTsConfigForTableauPublic(document, tsConfig);
  handleTsConfigForTelemetry(tsConfig);

  // Wait for all scripts to be loaded so we don't end up loading jsstrings twice
  onVqlScriptReady(() => {
    const { language } = getServerLanguageAndLocale(tsConfig);
    void loadStrings(document, language).then(
      (strings: TableauLocalizedStrings) => {
        updateDocumentTitle(document, strings, tsConfig);
      }
    );
  });
}

function handleTsConfigForTelemetry(tsConfig: TsConfig | null) {
  if (!tsConfig) {
    return;
  }

  setMetricsSuitesFilter(tsConfig.metricsFilter);
  setSessionType(tsConfig.vizqlSessionType);
}

function handleTsConfig(isUnitTestRun: boolean, tsConfig: TsConfig | null) {
  openEptMetricsContext(EptBreakdownMetricNames.handleTsConfig);
  onDocumentReady(() => {
    templateHtmlBasedOnTsConfig(isUnitTestRun, tsConfig);
    closeEptMetricsContext(EptBreakdownMetricNames.handleTsConfig);
  });
}

function withTimeout<T>(millis: number, promise: Promise<T>) {
  const timeout = new Promise<T>((_resolve, reject) =>
    setTimeout(() => reject(`Timed out after ${millis} ms.`), millis)
  );
  return Promise.race([promise, timeout]);
}

function reportError(reason: unknown) {
  console.error(reason);
  const errorDialog = new ErrorDialog(document);
  const preferredLanguages = getUserPreferredBrowserLanguages();
  const language =
    findFirstSupportedLanguageWithRegionAgnosticFallbackOrDefault(
      preferredLanguages
    );

  void loadStrings(document, language)
    .then(
      (strings: TableauLocalizedStrings) => {
        const dialogTitle = strings.getString("UnhandledExceptionTitle");
        const dialogHTML = (
          strings.getString("UnhandledExceptionMessage") as unknown as (
            ...args: unknown[]
          ) => string
        )(undefined, reason);
        errorDialog.overwriteTitle(dialogTitle).overwriteBodyHTML(dialogHTML);
      },
      () => {
        console.error("Unable to load localized strings");
      }
    )
    .then(() => {
      errorDialog.show();
    });
}

function conditionallyRetrieveAuthToken(): Promise<string | null> {
  return readStaticConfig()
    .then((config: StaticConfig) => {
      return new Promise<string | null>((resolve, reject) => {
        const isEmbedded = window.location !== window.parent.location;
        const shouldStopRetrievingToken =
          !config.isSalesforceNative || !isEmbedded;

        if (shouldStopRetrievingToken) {
          return resolve(null);
        }

        const parentOrigin = config.parentFrameOrigin;

        if (!parentOrigin) {
          return reject("parentFrameOrigin is not configured");
        }

        window.addEventListener("message", function (event) {
          if (
            parentOrigin !== "*" &&
            event.origin.toLowerCase() !== parentOrigin.toLowerCase()
          )
            return;

          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (event.data && typeof event.data.authToken === "string") {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
            return resolve(event.data.authToken);
          }

          if (
            event.data &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            event.data.parameters &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            typeof event.data.parameters.authToken === "string"
          ) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
            return resolve(event.data.parameters.authToken);
          }
        });

        const messageToParentFrame = {
          msgGuid: guid.raw(),
          msgType: "notification",
          notificationId: "ready-to-receive-auth-token",
          readyToReceiveAuthToken: true,
        };

        window.parent.postMessage(messageToParentFrame, parentOrigin);
      });
    })
    .catch((reason: Error) => {
      reportError(reason);
      return null;
    });
}
const startSessionThenBootstrap = (maybeAuthToken: string | null) => {
  const sessionRouteFromStorage = vizqlStorage.getSessionRoute(window.location);

  doShow(sessionRouteFromStorage, maybeAuthToken)
    .then(() => {
      bootstrap(maybeAuthToken);
    })
    .catch((reason: Error) => {
      reportError(reason);
    });
};

const main = () => {
  // For unit test runs, staticConfig will be added to the global scope before any VizClient scripts are loaded
  if (typeof staticConfig !== "undefined" && staticConfig.isUnitTestRun) {
    templateHtmlBasedOnTsConfig(true, tsConfig ?? null);
    bootstrap(null);

    return;
  }

  onDocumentReady(() => {
    updateInitialPublicPage(document);
    showSpinner(document);
  });

  onVisibilityChange();

  setupTypeStyle();

  readStaticConfig()
    .then((config: StaticConfig) => {
      if (config.isOffline) {
        doShowOffline()
          .then(() => bootstrap(null))
          .catch((reason: Error) => {
            reportError(reason);
          });
      } else {
        const timeToWaitForAuthTokenBeforeThrowingError = 5_000; // 5 seconds
        withTimeout(
          timeToWaitForAuthTokenBeforeThrowingError,
          conditionallyRetrieveAuthToken()
        )
          .then((maybeAuthToken) => startSessionThenBootstrap(maybeAuthToken))
          .catch((reason: Error) => {
            reportError(reason);
          });
      }
    })
    .catch((reason: Error) => {
      reportError(reason);
    });

  vizqlStorage
    .purgeExpiredSessions()
    .catch((reason: Error) =>
      console.error(`Vizql storage purge failed: ${reason.toString()}`)
    );
};

main();
